import React, { useEffect, useState } from "react";
import { Redirect, withRouter } from "react-router-dom";

import { auth } from "../../utils/firebaseUtils";
import * as SC from "./Billing.styled";

import logger from "../../utils/LoggerFrontend";
import Actions from "../../Actions";
import StatusStore from "../../Stores/StatusStore";
import { Store__System } from "@talentbait/shared-components";
import { withRefluxStore } from "../../Stores";

import LoadingView from "../../Components/LoadingView";

import {
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { ArrowFilterUp } from "../../Components/Icons/ArrowFilterUp";
import { ArrowFilterDown } from "../../Components/Icons/ArrowFilterDown";
import { ArrowsFilterAll } from "../../Components/Icons/ArrowsFilterAll";
import { TablePaginationArrowLeft } from "../../Components/Icons/TablePaginationArrowLeft";
import { TablePaginationArrowRight } from "../../Components/Icons/TablePaginationArrowRight";
import { IconArrowDown } from "../../Components/Icons";
import Skeleton from "./skeleton/Skeleton";
import { useTranslation } from "react-i18next";

function Filter({ column }) {
  const { filterVariant } = column.columnDef.meta || {};
  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = React.useMemo(
    () =>
      filterVariant === "range"
        ? []
        : Array.from(column.getFacetedUniqueValues().keys()).sort().slice(0, 5000),
    [column.getFacetedUniqueValues(), filterVariant]
  );

  return filterVariant === "range" ? (
    <div>
      <div className="flex space-x-2">
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] || "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] || "")}
          value={(columnFilterValue || [])[0] || ""}
          onChange={(value) => column.setFilterValue((old) => [value, old?.[1]])}
          placeholder={`Min ${
            column.getFacetedMinMaxValues()?.[0] !== undefined
              ? `(${column.getFacetedMinMaxValues()?.[0]})`
              : ""
          }`}
          className="w-24 border shadow rounded"
        />
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] || "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] || "")}
          value={(columnFilterValue || [])[1] || ""}
          onChange={(value) => column.setFilterValue((old) => [old?.[0], value])}
          placeholder={`Max ${
            column.getFacetedMinMaxValues()?.[1] ? `(${column.getFacetedMinMaxValues()?.[1]})` : ""
          }`}
          className="w-24 border shadow rounded"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : filterVariant === "select" ? (
    <select onChange={(e) => column.setFilterValue(e.target.value)} value={columnFilterValue || ""}>
      <option value="">All</option>
      {sortedUniqueValues.map((value) => (
        <option value={value} key={value}>
          {value}
        </option>
      ))}
    </select>
  ) : (
    <>
      <datalist id={column.id + "list"}>
        {sortedUniqueValues.map((value) => (
          <option value={value} key={value} />
        ))}
      </datalist>
      <DebouncedInput
        type="text"
        value={columnFilterValue || ""}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
        className="w-36 border shadow rounded"
        list={column.id + "list"}
      />
      <div className="h-1" />
    </>
  );
}

function DebouncedInput({ value: initialValue, onChange, debounce = 500, ...props }) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />;
}

const Billing = ({ user, userProfile, errorLoadingInvoices, match }) => {
  const orgaId = match.params.orgaId;
  const { t } = useTranslation();

  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5
  });

  const [invoiceList, setInvoiceList] = useState(Array(3).fill({}));

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("ASC");
  const [sumBudget, setSumBudget] = useState(0);
  const [sumUnpaid, setSumUnpaid] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [isLoadingOrga, setIsLoadingOrga] = useState(false);
  const [orgaData, setOrgaData] = useState(undefined);

  useEffect(() => {
    const loadInvoicesCompletedRef = Actions.loadInvoices.completed.listen((result) => {
      logger.debug("Billing:loadInvoices:completed", "called...", result);

      setInvoiceList(result.payload);
      setIsLoadingInvoices(false);
    });

    const loadInvoicesFailedRef = Actions.loadInvoices.failed.listen((err) => {
      logger.debug("Billing:loadInvoices:failed", "called...", err);
      setIsLoadingInvoices(false);
    });

    const loadHandlerCompletedRef = Actions.loadOrganization.completed.listen((orga) => {
      logger.debug("Billing:loadHandler:completed", "called...", orga);
      Actions.loadInvoices(orga.id);

      setIsLoadingInvoices(true);
      setIsLoadingOrga(false);
      setOrgaData(orga);
    });

    const loadHandlerFailedRef = Actions.loadOrganization.failed.listen(() => {
      logger.debug("Billing:loadHandler:failed", "called...");

      setIsLoadingOrga(false);
    });

    logger.debug("Billing:constructor", "called...", orgaId);

    Actions.loadOrganization(orgaId);

    return () => {
      loadInvoicesCompletedRef();
      loadInvoicesFailedRef();
      loadHandlerCompletedRef();
      loadHandlerFailedRef();
    };
  }, [orgaId]);

  const renderDesc = (text) => {
    text = text.replace("Social Media Booster für die Stellenanzeige", "");
    text = text.replace("der Firma: ", "");
    text = text.replace("über 30 Tage", "");

    return text.slice(0, 100) + "...";
  };

  const renderStatus = (status) => {
    return <SC.Status isPaid={status}>{status ? "Bezahlt" : "Offen"}</SC.Status>;
  };

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "number",
        id: "number",
        cell: (info) => info.getValue(),
        header: () => <span>{t("Invoice number")}</span>
      },
      {
        accessorFn: (row) => row.customerName,
        id: "customerName",
        cell: ({ row }) => renderDesc(row.original.lines[0].description || ""),
        header: () => <span>{t("Business name")}</span>
      },
      {
        accessorKey: "totalGrossAmount",
        header: () => "Betrag",
        cell: (info) => `${parseFloat(info.getValue()).toFixed(2)} €`
      },
      {
        accessorKey: "date",
        header: () => "Erstellt"
      },
      {
        accessorKey: "paid",
        header: () => "Status",
        cell: ({ row }) => renderStatus(row.original.paid)
      },
      {
        id: "download",
        type: "display",
        cell: ({ row }) => <SC.Download onClick={(event) => openInvoice(row.original.id, event)} />
      }
    ],
    [t]
  );

  const table = useReactTable({
    data: invoiceList,
    columns,
    state: {
      columnFilters,
      pagination
    },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(), // client-side filtering
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    getFacetedRowModel: getFacetedRowModel(), // client-side faceting
    getFacetedUniqueValues: getFacetedUniqueValues(), // generate unique values for select filter/autocomplete
    getFacetedMinMaxValues: getFacetedMinMaxValues(), // generate min/max values for range filter
    debugTable: true,
    debugHeaders: true,
    debugColumns: false
  });

  const openInvoice = (invoiceId, event) => {
    event.preventDefault();

    auth.currentUser.getIdToken(true).then(function (idToken) {
      window.open(`/api/v1/invoicing/invoices/${invoiceId}/pdf?idToken=${idToken}`);
    });
  };

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  const handlePage = (pageNumber) => {
    table.setPageIndex(pageNumber);
  };

  const getDisplayedPages = () => {
    const pages = [];

    if (pageCount <= 4) {
      // Show all pages if page count is less than or equal to 5
      for (let i = 0; i < pageCount; i++) {
        pages.push(i);
      }
    } else if (currentPage < 4) {
      // Show the first 3 pages, an ellipsis, and the last page
      pages.push(0, 1, 2, 3, "...", pageCount - 1);
    } else if (currentPage >= pageCount - 4) {
      // Show the first page, an ellipsis, and the last 5 pages
      pages.push(0, "...", pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1);
    } else {
      // Show current page in the middle with two pages on each side, an ellipsis, and the last page
      pages.push(0, "...", currentPage - 1, currentPage, currentPage + 1, "...", pageCount - 1);
    }

    return pages;
  };

  const orgas = userProfile && userProfile.orgas;

  if (!user) {
    return <Redirect to="/login" />;
  }

  const isRecruiter = orgas?.[orgaId]?.roleName === "RECRUITER";
  if (isRecruiter) {
    return <Redirect to="/" />;
  }

  if (orgaId && orgas && !orgas[orgaId]) {
    return (
      <LoadingView
        msg="Kein Zugriff auf die Rechnungen der der Organization."
        showSpinner={false}
      />
    );
  }

  if (errorLoadingInvoices) {
    return (
      <LoadingView
        msg="Kein Zugriff auf die Rechnungen der der Organization."
        showSpinner={false}
      />
    );
  }

  if (!invoiceList) {
    return <LoadingView msg="Lade Rechnungen..." showSpinner />;
  }

  return (
    <SC.Layout>
      <SC.TableTitle>
        {t("Invoice overview")} ({invoiceList.length})
      </SC.TableTitle>
      <SC.Table>
        <SC.TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();

                return (
                  <th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                      <>
                        <SC.TableHeadFilter
                          canSort={canSort}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort &&
                            ({
                              asc: <ArrowFilterUp />,
                              desc: <ArrowFilterDown />
                            }[header.column.getIsSorted() || ""] ?? <ArrowsFilterAll />)}
                        </SC.TableHeadFilter>
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </SC.TableHead>
        <SC.TableBody>
          {isLoadingInvoices
            ? invoiceList.map((invoice, index) => {
                return (
                  <tr key={index}>
                    {columns.map((column, index) => {
                      return (
                        <td key={index}>
                          <Skeleton />
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            : table.getRowModel().rows.map((row) => {
                return (
                  <tr key={row.original.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })}
        </SC.TableBody>
      </SC.Table>
      <div style={{ marginTop: "3%" }} />
      <SC.PaginationWrapper>
        <SC.PagesWrapper>
          <SC.PageArrow
            as={TablePaginationArrowLeft}
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          />
          <SC.PageNumberWrapper>
            {getDisplayedPages().map((pageNumber, index) =>
              typeof pageNumber === "number" ? (
                <SC.PageNumber
                  key={index}
                  onClick={() => handlePage(pageNumber)}
                  isActive={pageNumber === currentPage}
                >
                  {pageNumber + 1}
                </SC.PageNumber>
              ) : (
                <SC.PageNumber key={index}>...</SC.PageNumber>
              )
            )}
          </SC.PageNumberWrapper>
          <SC.PageArrow
            as={TablePaginationArrowRight}
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          />
        </SC.PagesWrapper>
        <SC.ResultsWrapper></SC.ResultsWrapper>
        <SC.SelectContainer>
          <SC.CustomSelect
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
          >
            {[5, 10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {t("Show")} {pageSize}
              </option>
            ))}
          </SC.CustomSelect>
          <SC.ArrowIconWrapper>
            <IconArrowDown />
          </SC.ArrowIconWrapper>
        </SC.SelectContainer>
      </SC.PaginationWrapper>
    </SC.Layout>
  );
};

export default withRouter(
  withRefluxStore(
    Billing,
    [Store__System, StatusStore],
    ["user", "userProfile", "errorLoadingInvoices"]
  )
);
